import { Box } from '@squareup/dex-ui-shared-base';
import { card } from '@squareup/dex-ui-shared-markdown-card';
import React, {
  Children,
  cloneElement,
  FC,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from 'react';

interface MdxCardProps {
  title?: string;
  icon?: ReactNode;
  href?: string;
  iconPath?: string;
  iconAlt?: string;
  iconWidth?: number;
  iconHeight?: number;
  iconSize?: number;
  forceBorderless?: boolean;
  paddingSize?: 'small' | 'medium' | 'none';
  hoverStyle?: 'shadow' | 'darken' | 'none';
  disableHrefOnNarrow?: boolean;
  disableCardHref?: boolean;
  iconBorder?: boolean;
  inTab?: boolean;
  linkLabel?: string;
}

const MarkdownCard = card.component.value as FC<
  PropsWithChildren<MdxCardProps>
>;

function resolveIconPath(icon?: ReactNode) {
  if (typeof icon !== 'string') {
    return undefined;
  }

  // Fern card icons are sometimes symbolic names such as "rocket". DocCard
  // expects an image path, so only pass through values Next/Image can load.
  return icon.startsWith('/') || /^https?:\/\//.test(icon) ? icon : undefined;
}

function renderInlineIcon(icon?: ReactNode) {
  return isValidElement(icon) ? icon : null;
}

function resolveIconDimensions(props: MdxCardProps) {
  const sizeFromProp = props.iconSize ? props.iconSize * 4 : undefined;
  const iconWidth = props.iconWidth ?? sizeFromProp;
  const iconHeight = props.iconHeight ?? sizeFromProp;
  return {
    ...(iconWidth !== undefined ? { iconWidth } : {}),
    ...(iconHeight !== undefined ? { iconHeight } : {}),
  };
}

const MdxCard: FC<PropsWithChildren<MdxCardProps>> = ({
  title,
  href,
  icon,
  iconPath: iconPathProp,
  iconAlt,
  iconSize,
  children,
  ...markdownCardProps
}) => {
  const iconPath = iconPathProp ?? resolveIconPath(icon);
  const iconDimensions = resolveIconDimensions({
    ...(iconSize !== undefined ? { iconSize } : {}),
    ...markdownCardProps,
  });

  return (
    <MarkdownCard
      {...(href ? { href } : {})}
      {...(iconPath ? { iconPath, iconAlt: iconAlt || title || '' } : {})}
      {...iconDimensions}
      {...markdownCardProps}
      {...(markdownCardProps.linkLabel || title
        ? { linkLabel: markdownCardProps.linkLabel || title }
        : {})}
    >
      {!iconPath && renderInlineIcon(icon)}
      {title ? (
        <p>
          <strong>{title}</strong>
        </p>
      ) : null}
      {children}
    </MarkdownCard>
  );
};

MdxCard.displayName = 'MdxCard';

interface MdxCardGroupProps {
  cols?: number;
}

const MdxCardGroup: FC<PropsWithChildren<MdxCardGroupProps>> = ({
  cols = 2,
  children,
}) => {
  const useGrid = cols > 1;
  const enhancedChildren = useGrid
    ? Children.map(children, (child) => {
        if (!isValidElement(child)) {
          return child;
        }

        const cardChild = child as ReactElement<MdxCardProps>;
        return cloneElement(cardChild, {
          forceBorderless: cardChild.props.forceBorderless ?? true,
          paddingSize: cardChild.props.paddingSize ?? 'small',
          hoverStyle: cardChild.props.hoverStyle ?? 'darken',
        });
      })
    : children;

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '16px',
      }}
    >
      {enhancedChildren}
    </Box>
  );
};

MdxCardGroup.displayName = 'MdxCardGroup';

export { MdxCard, MdxCardGroup };
